<template>
  <popup
    ref="popupRefs"
    :async="true"
    width="760px"
    top="5vh"
    title="查看线索"
    custom-class="clue-detail-dialog"
    :confirm-button="false"
    cancel-button="关闭"
    @close="onClose"
  >
    <div class="clue-detail" v-loading="loading">
      <section class="detail-section detail-section--inquiry">
        <div class="detail-section__head">
          <span class="detail-section__icon">
            <i class="el-icon-document"></i>
          </span>
          <strong>询盘信息</strong>
        </div>
        <div class="detail-section__body">
          <div v-for="item in inquiryRows" :key="item.key" class="detail-row">
            <div class="detail-row__label">{{ item.label }}</div>
            <div class="detail-row__value">
              <div
                v-if="attachmentLinks(item).length"
                class="detail-attachments"
              >
                <a
                  v-for="link in attachmentLinks(item)"
                  :key="link.url"
                  class="detail-link-card"
                  :href="link.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i class="el-icon-paperclip"></i>
                  <span>{{ link.name || link.url }}</span>
                  <i class="el-icon-top-right"></i>
                </a>
              </div>
              <a
                v-else-if="isLinkValue(item.value)"
                class="detail-link-card"
                :href="linkUrl(item.value)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="el-icon-link"></i>
                <span>{{ cleanValue(item.value) }}</span>
                <i class="el-icon-copy-document"></i>
              </a>
              <span v-else>{{ displayValue(item.value) }}</span>
            </div>
          </div>
          <div v-if="!inquiryRows.length" class="detail-empty">
            暂无线索内容
          </div>
        </div>
      </section>

      <section class="detail-section detail-section--source">
        <div class="detail-section__head">
          <span class="detail-section__icon">
            <i class="el-icon-s-promotion"></i>
          </span>
          <strong>来源信息</strong>
        </div>
        <div class="detail-section__body">
          <div v-for="item in sourceRows" :key="item.key" class="detail-row">
            <div class="detail-row__label">{{ item.label }}</div>
            <div class="detail-row__value">
              <a
                v-if="isLinkValue(item.value)"
                class="detail-url"
                :href="linkUrl(item.value)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>{{ cleanValue(item.value) }}</span>
                <i class="el-icon-top-right"></i>
              </a>
              <span v-else>{{ sourceDisplayValue(item) }}</span>
            </div>
          </div>
          <div v-if="!sourceRows.length" class="detail-empty">暂无来源信息</div>
        </div>
      </section>

      <section
        v-if="geoRows.length"
        class="detail-section detail-section--source"
      >
        <div class="detail-section__head">
          <span class="detail-section__icon"
            ><i class="el-icon-location-information"></i
          ></span>
          <strong>IP 位置解析</strong>
        </div>
        <div class="detail-section__body">
          <div v-for="item in geoRows" :key="item.label" class="detail-row">
            <div class="detail-row__label">{{ item.label }}</div>
            <div class="detail-row__value">{{ item.value }}</div>
          </div>
        </div>
      </section>

      <section class="detail-section detail-section--logs">
        <div class="detail-section__head">
          <span class="detail-section__icon">
            <i class="el-icon-warning-outline"></i>
          </span>
          <strong>异常记录</strong>
        </div>
        <div class="detail-section__body">
          <div
            v-for="item in deliveryLogs"
            :key="item.id"
            class="detail-log-row"
          >
            <div class="detail-log-row__meta">
              <el-tag
                size="small"
                :type="item.event_type === 'failed' ? 'danger' : 'warning'"
              >
                {{ eventTypeLabel(item.event_type) }}
              </el-tag>
              <el-tag
                size="small"
                :type="item.attempt_type === 'manual' ? 'success' : 'info'"
              >
                {{ attemptTypeLabel(item.attempt_type) }}
              </el-tag>
              <span>{{
                formatDate(item.create_time, "yyyy-mm-dd hh:MM:ss")
              }}</span>
            </div>
            <div class="detail-log-row__reason">
              {{ item.reason_message || item.reason_code || "-" }}
            </div>
            <div v-if="item.matched_keyword" class="detail-log-row__keyword">
              命中关键词：{{ item.matched_keyword }}
            </div>
          </div>
          <div v-if="!deliveryLogs.length" class="detail-empty">
            暂无屏蔽或失败记录
          </div>
        </div>
      </section>
    </div>
  </popup>
</template>

<script>
import Popup from "@/components/popup/index.vue";

import { apiDetailClue } from "@/api/clue";
import { formatDate } from "@/utils/util";

const GEO_REFRESH_INTERVAL = 3000;

export default {
  components: {
    Popup,
  },
  data() {
    return {
      identity: null,
      loading: false,
      geoRefreshTimer: null,
      geoRefreshInFlight: false,
      form: {
        content: {},
        delivery_logs: [],
        geo_enrichment: {},
      },
    };
  },
  computed: {
    inquiryRows() {
      const content = this.withDefaultSourceLocation(this.form.content || {});
      const rows = Object.keys(content)
        .filter(
          (key) =>
            !this.isHiddenKey(key) &&
            !this.isSourceKey(key) &&
            this.normalizeKey(key) !== "customfields" &&
            this.isMeaningfulValue(content[key])
        )
        .map((key) => ({
          key,
          label: this.fieldLabel(key),
          value: content[key],
        }));
      return rows.concat(this.customFieldRows(content.Custom_Fields));
    },
    sourceRows() {
      const content = this.withDefaultSourceLocation(this.form.content || {});
      const definitions = [
        {
          key: "Origin_Page_Url",
          aliases: [
            "Origin_Page_Url",
            "Origin_Url",
            "origin_url",
            "Url",
            "url",
          ],
          label: "来源页面",
        },
        {
          key: "Origin_Ip",
          aliases: ["Origin_Ip", "Ip", "IP", "remote_ip"],
          label: "IP地址",
        },
        { key: "Origin_Country", aliases: ["Origin_Country"], label: "国家" },
        {
          key: "Origin_Region",
          aliases: ["Origin_Region"],
          label: "地区/省州",
        },
        { key: "Origin_City", aliases: ["Origin_City"], label: "城市" },
        {
          key: "Origin_Submission_Duration_Ms",
          aliases: ["Origin_Submission_Duration_Ms"],
          label: "提交时长",
          requireValue: true,
        },
      ];
      return definitions
        .map((definition) => {
          const alias = definition.aliases.find((key) =>
            Object.prototype.hasOwnProperty.call(content, key)
          );
          if (!alias) return null;
          const value = content[alias];
          if (definition.requireValue && !this.isMeaningfulValue(value)) {
            return null;
          }
          return {
            key: definition.key,
            label: definition.label,
            value,
          };
        })
        .filter(Boolean);
    },
    deliveryLogs() {
      return Array.isArray(this.form.delivery_logs)
        ? this.form.delivery_logs
        : [];
    },
    geoRows() {
      const geo = this.form.geo_enrichment || {};
      if (!geo.status) return [];
      const statusLabels = {
        pending: "等待解析",
        success: "解析成功",
        unknown: "已查询，位置未知（允许转发）",
        failed: "解析失败（未转发）",
        skipped: "IP 不可查询（未转发）",
        bypassed: "本机 IP 已放行（未查询）",
      };
      const providerLabels = {
        "local-bypass": "本机测试放行",
      };
      const rows = [
        { label: "状态", value: statusLabels[geo.status] || geo.status },
        {
          label: "来源",
          value: providerLabels[geo.provider] || geo.provider || "-",
        },
        { label: "IPWHOIS 调用", value: `${geo.ipwhois_attempts || 0} 次` },
        {
          label: "Ipregistry 调用",
          value: `${geo.ipregistry_attempts || 0} 次`,
        },
      ];
      if (
        geo.lookup_duration_ms !== null &&
        geo.lookup_duration_ms !== undefined
      ) {
        rows.push({
          label: "解析耗时",
          value: this.formatDuration(geo.lookup_duration_ms),
        });
      }
      if (geo.error_message)
        rows.push({ label: "结果说明", value: geo.error_message });
      if (geo.checked_at) {
        rows.push({
          label: "完成时间",
          value: formatDate(geo.checked_at, "yyyy-mm-dd hh:MM:ss"),
        });
      }
      return rows;
    },
  },
  beforeDestroy() {
    this.stopGeoRefresh();
  },
  methods: {
    formatDate,
    getDetail(silent = false) {
      if (!this.identity || this.geoRefreshInFlight) {
        return Promise.resolve();
      }
      const identity = this.identity;
      if (!silent) {
        this.loading = true;
      }
      this.geoRefreshInFlight = true;
      return apiDetailClue({ id: identity })
        .then((resp) => {
          if (this.identity !== identity) return;
          Object.keys(resp || {}).forEach((item) => {
            this.$set(this.form, item, resp[item]);
          });
          this.syncGeoRefresh();
        })
        .finally(() => {
          this.geoRefreshInFlight = false;
          if (!silent) {
            this.loading = false;
          }
        });
    },
    isGeoPending() {
      return (this.form.geo_enrichment || {}).status === "pending";
    },
    syncGeoRefresh() {
      if (this.isGeoPending()) {
        this.startGeoRefresh();
        return;
      }
      this.stopGeoRefresh();
    },
    startGeoRefresh() {
      if (this.geoRefreshTimer) return;
      this.geoRefreshTimer = window.setInterval(() => {
        this.getDetail(true);
      }, GEO_REFRESH_INTERVAL);
    },
    stopGeoRefresh() {
      if (!this.geoRefreshTimer) return;
      window.clearInterval(this.geoRefreshTimer);
      this.geoRefreshTimer = null;
    },
    isSourceKey(key) {
      return /^origin_/i.test(String(key || ""));
    },
    customFieldRows(fields) {
      if (!Array.isArray(fields)) return [];
      return fields
        .filter(
          (item) =>
            item &&
            typeof item === "object" &&
            this.normalizeKey(item.type) !== "quiz" &&
            this.isMeaningfulValue(item.value)
        )
        .map((item, index) => {
          const field = String(item.field || "").trim();
          const label = String(item.label || field).trim();
          return {
            key: `custom:${field || index}`,
            label: this.fieldLabel(label || field || "扩展字段"),
            value: item.value,
          };
        });
    },
    isMeaningfulValue(value) {
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) {
        return value.some((item) => this.isMeaningfulValue(item));
      }
      if (value && typeof value === "object") {
        return Object.keys(value).some((key) =>
          this.isMeaningfulValue(value[key])
        );
      }
      return String(value).trim() !== "";
    },
    isHiddenKey(key) {
      const normalized = this.normalizeKey(key);
      return [
        "raw",
        "mathquiz",
        "protocol",
        "protocolversion",
        "origin_cms",
        "origincms",
        "origin_plugin",
        "originplugin",
        "origin_plugin_version",
        "originpluginversion",
        "customfields",
      ].includes(normalized);
    },
    normalizeKey(key) {
      return String(key || "")
        .toLowerCase()
        .replace(/[\s_\-\t]+/g, "");
    },
    fieldLabel(key) {
      const normalized = this.normalizeKey(key);
      const labels = {
        yourname: "姓名",
        name: "姓名",
        姓名: "姓名",
        称呼: "姓名",
        contactname: "姓名",
        联系人: "姓名",
        youremail: "邮箱",
        mail: "邮箱",
        email: "邮箱",
        邮箱: "邮箱",
        电子邮箱: "邮箱",
        yourphone: "手机号",
        phone: "手机号",
        tel: "手机号",
        mobile: "手机号",
        手机: "手机号",
        手机号: "手机号",
        电话: "手机号",
        yourcountry: "国家",
        country: "国家",
        国家: "国家",
        company: "公司名称",
        公司: "公司名称",
        公司名称: "公司名称",
        yourmessage: "留言内容",
        message: "留言内容",
        content: "留言内容",
        内容: "留言内容",
        留言: "留言内容",
        留言内容: "留言内容",
        yourfile: "附件",
        attachments: "附件",
        attachment: "附件",
        file: "附件",
        files: "附件",
        附件: "附件",
        originip: "IP地址",
        originurl: "来源页面",
        origincountry: "国家",
        originregion: "地区/省州",
        origincity: "城市",
        origindistrict: "区县",
        originform: "表单名称",
      };
      return labels[normalized] || key;
    },
    withDefaultSourceLocation(content) {
      const rows = {
        ...content,
      };
      if (this.normalizeKey(rows.Origin_Cms) !== "eyoucms") {
        return rows;
      }
      [
        "Origin_Country",
        "Origin_Region",
        "Origin_City",
        "Origin_District",
      ].forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(rows, key)) {
          rows[key] = "";
        }
      });
      return rows;
    },
    isAttachmentKey(key) {
      const normalized = String(key || "").toLowerCase();
      return (
        normalized.includes("attachment") ||
        normalized.includes("file") ||
        normalized.includes("upload") ||
        normalized.includes("附件")
      );
    },
    attachmentLinks(item) {
      if (!this.isAttachmentKey(item.key)) return [];
      const values = Array.isArray(item.value) ? item.value : [item.value];
      return values
        .map((value) => {
          if (value && typeof value === "object") {
            const url = String(value.url || value.path || "").trim();
            return {
              name: String(value.name || "").trim(),
              url,
            };
          }
          const url = this.cleanValue(value);
          return {
            name: "",
            url,
          };
        })
        .filter((item) => /^https?:\/\//i.test(item.url));
    },
    isLinkValue(value) {
      return /^https?:\/\//i.test(this.cleanValue(value));
    },
    linkUrl(value) {
      return this.cleanValue(value);
    },
    cleanValue(value) {
      if (Array.isArray(value)) {
        return value.length ? this.cleanValue(value[0]) : "";
      }
      const text = String(value || "").trim();
      if (!text) return "";
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.length ? this.cleanValue(parsed[0]) : "";
        }
      } catch (error) {
        // Keep non-JSON strings as-is.
      }
      return text
        .replace(/^【/, "")
        .replace(/】$/, "")
        .replace(/^\["/, "")
        .replace(/"\]$/, "")
        .replace(/^"|"$/g, "");
    },
    displayValue(value) {
      if (Array.isArray(value)) {
        return value.length ? value.map(this.cleanValue).join("，") : "-";
      }
      if (value && typeof value === "object") {
        return JSON.stringify(value);
      }
      return value === "" || value === null || value === undefined
        ? "-"
        : this.cleanValue(value);
    },
    sourceDisplayValue(item) {
      if (item.key === "Origin_Submission_Duration_Ms") {
        return this.formatDuration(item.value);
      }
      return this.displayValue(item.value);
    },
    formatDuration(value) {
      const milliseconds = Number(value);
      if (!Number.isFinite(milliseconds) || milliseconds < 0) return "-";
      if (milliseconds < 1000) return `${Math.round(milliseconds)} 毫秒`;
      const seconds = milliseconds / 1000;
      return `${seconds.toFixed(seconds < 10 ? 2 : 1).replace(/\.0+$/, "")} 秒`;
    },
    eventTypeLabel(value) {
      return (
        {
          blocked: "屏蔽",
          failed: "失败",
        }[value] || "未知"
      );
    },
    attemptTypeLabel(value) {
      return (
        {
          automatic: "自动转发",
          manual: "手动转发",
        }[value] || "未知"
      );
    },
    openDialog(id) {
      this.stopGeoRefresh();
      this.identity = id;
      this.form = {
        content: {},
        delivery_logs: [],
        geo_enrichment: {},
      };
      if (this.identity) {
        this.getDetail();
      }
      this.$refs.popupRefs.open();
    },
    onClose() {
      this.stopGeoRefresh();
      this.identity = null;
    },
  },
};
</script>

<style lang="scss">
.clue-detail-dialog {
  border-radius: 8px;
  overflow: hidden;

  .el-dialog__header {
    padding: 28px 32px 14px;
  }

  .el-dialog__title,
  .el-dialog__header div[slot="title"] {
    color: #111827;
    font-size: 22px !important;
    font-weight: 700;
    line-height: 30px;
  }

  .el-dialog__headerbtn {
    top: 30px;
    right: 30px;
    font-size: 22px;
  }

  .el-dialog__body {
    padding: 14px 28px 0;
  }

  .el-dialog__footer {
    padding: 14px 28px 22px;
    border-top: 1px solid #edf1f7;
  }

  .dialog-footer .el-button {
    min-width: 88px;
    height: 38px;
    color: #111827;
    font-size: 15px;
    font-weight: 700;
    border-color: #d9e2ef;
  }
}

.clue-detail {
  max-height: 72vh;
  overflow-y: auto;
  padding-right: 4px;
}

.detail-section {
  margin-bottom: 14px;
  border: 1px solid #e5ebf4;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(17, 24, 39, 0.04);

  &__head {
    min-height: 56px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 18px;
    border-bottom: 1px dashed #d8e3f3;
    background: linear-gradient(90deg, #f3f7ff 0%, #ffffff 100%);

    strong {
      color: #111827;
      font-size: 17px;
      line-height: 24px;
    }
  }

  &__icon {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #1b6cff;
    background: #dfeaff;

    i {
      font-size: 18px;
    }
  }

  &__body {
    padding: 10px 18px 0;
  }

  &--source {
    .detail-section__head {
      border-bottom-color: #d7efd9;
      background: linear-gradient(90deg, #effbf2 0%, #ffffff 100%);
    }

    .detail-section__icon {
      color: #20a45b;
      background: #dcf7e4;
    }
  }

  &--logs {
    .detail-section__head {
      border-bottom-color: #ffe0b8;
      background: linear-gradient(90deg, #fff7ea 0%, #ffffff 100%);
    }

    .detail-section__icon {
      color: #e47b00;
      background: #fff0d8;
    }
  }
}

.detail-row {
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  min-height: 44px;
  border-bottom: 1px solid #edf1f7;

  &:last-child {
    border-bottom: none;
  }

  &__label {
    color: #53627a;
    font-size: 15px;
    line-height: 22px;
    word-break: break-word;
  }

  &__value {
    min-width: 0;
    color: #111827;
    font-size: 15px;
    line-height: 22px;
    word-break: break-word;
  }
}

.detail-link-card {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  min-height: 42px;
  padding: 9px 12px;
  color: #1269f2;
  border: 1px solid #cfe0ff;
  border-radius: 6px;
  background: #f6f9ff;
  box-sizing: border-box;
  text-decoration: none;

  span {
    flex: 1;
    min-width: 0;
    word-break: break-all;
  }

  i {
    flex: 0 0 auto;
    font-size: 18px;
  }
}

.detail-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-url {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  color: #1269f2;
  text-decoration: none;

  span {
    min-width: 0;
    word-break: break-all;
  }
}

.detail-empty {
  padding: 22px 0;
  color: #9aa7b8;
  text-align: center;
}

.detail-log-row {
  padding: 12px 0;
  border-bottom: 1px solid #edf1f7;

  &:last-child {
    border-bottom: none;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #8a97aa;
    font-size: 13px;
    line-height: 22px;
  }

  &__reason {
    margin-top: 8px;
    color: #111827;
    font-size: 14px;
    line-height: 22px;
    word-break: break-word;
  }

  &__keyword {
    margin-top: 6px;
    color: #e47b00;
    font-size: 13px;
    line-height: 20px;
    word-break: break-word;
  }
}
</style>
